import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  NewspaperIcon,
  CalendarIcon,
  TagIcon
} from '@heroicons/react/24/outline';

function articleMainImageSrc(m) {
  if (!m) return null;
  if (typeof m === 'string') return m.startsWith('http') ? m : `${process.env.REACT_APP_API_URL}${m}`;
  if (m.url) return m.url;
  if (m.legacyPath) return `${process.env.REACT_APP_API_URL}${m.legacyPath}`;
  return null;
}

export default function Actualites() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasActiveAdhesion, setHasActiveAdhesion] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkAdhesions();
    fetchArticles();
    fetchAllTags();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedTag]);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const checkAdhesions = async () => {
    if (!user) {
      setHasActiveAdhesion(false);
      return;
    }
    if (user?.role === 'admin') {
      setHasActiveAdhesion(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const currentYear = new Date().getFullYear();
      const activeAdhesion = response.data.find(
        adh => adh.annee === currentYear && adh.status === 'actif'
      );
      
      setHasActiveAdhesion(!!activeAdhesion);
    } catch (error) {
      console.error('Erreur lors de la vérification des adhésions:', error);
      setHasActiveAdhesion(false);
    }
  };

  const fetchArticles = async () => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/actualites/articles?page=${page}&limit=9`;
      if (selectedTag) {
        url += `&tag=${encodeURIComponent(selectedTag)}`;
      }
      const response = await axios.get(url, {
        headers: authHeaders(),
      });
      setArticles(response.data.articles);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Erreur lors de la récupération des articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTags = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/actualites/articles?page=1&limit=1000`,
        { headers: authHeaders() }
      );
      // Extraire tous les tags uniques
      const tags = new Set();
      response.data.articles.forEach(article => {
        article.tags?.forEach(tag => tags.add(tag));
      });
      setAllTags(Array.from(tags).sort());
    } catch (error) {
      console.error('Erreur lors de la récupération des tags:', error);
    }
  };

  const handleTagFilter = (tag) => {
    if (selectedTag === tag) {
      setSelectedTag(null);
    } else {
      setSelectedTag(tag);
    }
    setPage(1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'publie': 'bg-green-100 text-green-800',
      'brouillon': 'bg-gray-100 text-gray-800',
      'programme': 'bg-blue-100 text-blue-800'
    };
    const labels = {
      'publie': 'Publié',
      'brouillon': 'Brouillon',
      'programme': 'Programmé'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[statut]}`}>
        {labels[statut]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mt-8">Actualités</h1>
              <p className="mt-2 text-sm text-gray-600">
                Actualités et informations pour les apiculteurs
              </p>
            </div>
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin/actualites')}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
              >
                <NewspaperIcon className="h-5 w-5 mr-2" />
                Gérer les articles
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtres par tags (articles visibles par l’utilisateur, y compris publics anonymes) */}
      {(hasActiveAdhesion || user?.role === 'admin' || allTags.length > 0) ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-2 mb-2">
              <TagIcon className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtrer par catégorie :</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleTagFilter(null)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  !selectedTag
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tous
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagFilter(tag)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedTag === tag
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            {selectedTag && (
              <p className="mt-2 text-xs text-gray-500">
                {articles.length} article(s) avec le tag "{selectedTag}"
              </p>
            )}
          </div>
        </div>
      ) : null}

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {!hasActiveAdhesion && user?.role !== 'admin' && articles.length > 0 && (
          <div className="mb-6 rounded-lg border border-indigo-100 bg-indigo-50/80 px-4 py-3 text-sm text-indigo-900">
            Les actualités <strong>réservées aux adhérents</strong> sont disponibles après connexion avec une adhésion active pour l'année en cours.
            {user ? (
              <button type="button" onClick={() => navigate('/adhesion')} className="ml-2 font-semibold underline">
                Adhérer
              </button>
            ) : (
              <>
                {' '}
                <button type="button" onClick={() => navigate('/login')} className="font-semibold underline">
                  Se connecter
                </button>
                {' '}ou{' '}
                <button type="button" onClick={() => navigate('/adhesion')} className="font-semibold underline">
                  adhérer
                </button>
                .
              </>
            )}
          </div>
        )}
        {articles.length === 0 ? (
          <div className="text-center py-12">
            <NewspaperIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun article</h3>
            <p className="mt-1 text-sm text-gray-500">
              {hasActiveAdhesion || user?.role === 'admin'
                ? 'Aucun article ne correspond à votre sélection.'
                : 'Aucun article public pour le moment. Les actualités réservées aux adhérents apparaissent après connexion.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <div
                  key={article._id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
                  onClick={() => navigate(`/actualites/${article.slug}`)}
                >
                  {/* Image */}
                  {articleMainImageSrc(article.imagePrincipale) ? (
                    <img
                      src={articleMainImageSrc(article.imagePrincipale)}
                      alt={article.titre}
                      className="w-full h-64 object-cover"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                      <NewspaperIcon className="h-16 w-16 text-white opacity-50" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6">

                    {/* Title */}
                    <h2 className="text-xl font-semilbold text-gray-900 mb-2 line-clamp-2">
                      {article.titre}
                    </h2>

                    {/* Excerpt */}
                    {article.extrait && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-3">
                        {article.extrait}
                      </p>
                    )}

                    {/* Footer */}
                    {/* Meta */}
                    <div className="flex items-center justify-between pt-4">
                      <div className="flex items-center text-xs text-gray-500">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {formatDate(article.datePublication || article.createdAt)}
                      </div>
                      {user?.role === 'admin' && getStatutBadge(article.statut)}
                      {/* <div className="flex items-center text-xs text-gray-500">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {article.auteur?.prenom} {article.auteur?.nom}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        {article.vues} vues
                      </div> */}
                    </div>

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {article.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700"
                          >
                            <TagIcon className="h-3 w-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {page} sur {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
